/**
 * PeriodSelector component
 * Requirements: 16.7 - Period selection UI (calendar picker, preset periods)
 */
import { useState, useCallback } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Grid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  subWeeks,
  subMonths,
  subYears,
  startOfDay,
  endOfDay,
} from 'date-fns';
import type { DateRange, PeriodPreset } from '../types';

export interface PeriodSelectorProps {
  selectedPreset: PeriodPreset;
  dateRange: DateRange;
  onChange: (preset: PeriodPreset, dateRange: DateRange) => void;
}

/**
 * Preset period configurations
 */
const presetConfig: { value: PeriodPreset; label: string }[] = [
  { value: 'week', label: '1週間' },
  { value: 'month', label: '1ヶ月' },
  { value: '3months', label: '3ヶ月' },
  { value: '6months', label: '6ヶ月' },
  { value: 'year', label: '1年' },
  { value: 'custom', label: 'カスタム' },
];

/**
 * Calculate date range from preset
 */
const calculateDateRange = (preset: PeriodPreset): DateRange => {
  const now = new Date();
  const endDate = endOfDay(now);

  switch (preset) {
    case 'week':
      return { startDate: startOfDay(subWeeks(now, 1)), endDate };
    case 'month':
      return { startDate: startOfDay(subMonths(now, 1)), endDate };
    case '3months':
      return { startDate: startOfDay(subMonths(now, 3)), endDate };
    case '6months':
      return { startDate: startOfDay(subMonths(now, 6)), endDate };
    case 'year':
      return { startDate: startOfDay(subYears(now, 1)), endDate };
    case 'custom':
      return { startDate: null, endDate: null };
    default:
      return { startDate: startOfDay(subMonths(now, 1)), endDate };
  }
};

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedPreset,
  dateRange,
  onChange,
}) => {
  const handlePresetChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newPreset: PeriodPreset | null) => {
      if (newPreset) {
        const newDateRange = calculateDateRange(newPreset);
        onChange(newPreset, newDateRange);
      }
    },
    [onChange]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      onChange('custom', { ...dateRange, startDate: date });
    },
    [dateRange, onChange]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      onChange('custom', { ...dateRange, endDate: date });
    },
    [dateRange, onChange]
  );

  return (
    <Paper data-testid="period-selector" sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={selectedPreset}
          exclusive
          onChange={handlePresetChange}
          aria-label="期間選択"
          size="small"
          sx={{ flexWrap: 'wrap' }}
        >
          {presetConfig.map(({ value, label }) => (
            <ToggleButton
              key={value}
              value={value}
              aria-label={label}
              aria-pressed={selectedPreset === value}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {selectedPreset === 'custom' && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label="開始日"
              value={dateRange.startDate}
              onChange={handleStartDateChange}
              maxDate={dateRange.endDate || undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  inputProps: {
                    'aria-label': '開始日',
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label="終了日"
              value={dateRange.endDate}
              onChange={handleEndDateChange}
              minDate={dateRange.startDate || undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  inputProps: {
                    'aria-label': '終了日',
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};
